<template>
	<div>
		<div class="card shadow-sm mb-4">
			<div class="card-header">
				<button class="btn btn-light" style="position: absolute; right: 0.5em; top: 0.5em;" @click.prevent="refreshSchema"><i class="fas fa-sync-alt"></i> Refresh</button>
				<h4 class="my-0 font-weight-normal">Schema Settings</h4>
			</div>
			<div class="card-body">
				<form autocomplete="off">
					<div class="form-group">
						<label for="schemaSettings_id">Schema Full Name</label>
						<input :value="schemaName" type="text" class="form-control" id="schemaSettings_name" aria-describedby="schemaSettings_nameHelp" disabled>
						<small id="schemaSettings_titleHelp" class="form-text text-muted">This is used within $schema fields of structured objects.</small>
					</div>
					<div class="form-group">
						<label for="schemaSettings_desc">Description</label>
						<textarea v-model="schemaSettings.description" type="text" class="form-control" id="schemaSettings_desc" aria-describedby="schemaSettings_descHelp" placeholder="Enter description" rows="2"></textarea>
						<small id="schemaSettings_descHelp" class="form-text text-muted">Descriptive details (e.g. "This is a testing Schema")</small>
					</div>
					<div class="form-check mb-3">
						<input v-model="schemaSettings.abstract" type="checkbox" class="form-check-input" id="schema_abstract">
						<label class="form-check-label" for="schema_abstract">Abstract <small class="text-muted">(objects based on this exact schema will not be considered valid)</small></label>
					</div>

					<button @click.prevent="toggleMixins()" class="btn mt-1 mb-2" :class="{ 'btn-secondary': showMixins, 'btn-light': !showMixins }"><i class="fas fa-vials"></i> Schema Mixins</button>
					<div v-if="showMixins" class="border col-12 mb-2" :id="'editor-mixins-'+_uid"></div>

					<h4 class="my-2 font-weight-normal">This Schema's Own Fields</h4>
					<!-- object value wrapper (OUTER) -->
					<div class="container p-0 mb-3" style="padding-right: 50px !important;" ref="schemaFields">
						<schema-editor-field v-if="!field._delete" v-for="(field,index) in standardFieldsHierarchical" :key="index" :field="field" :source="standardFields" :index="index" :schemas="schemaLookup" :add-field-function="addSchemaField" />
						<button @click.prevent="addSchemaField( )" class="btn btn-info mt-1">Add Field to Schema</button>
					</div>

					<template v-if="!isRootSchema( )">
						<h4 class="my-2 font-weight-normal">Derived and Overridden Fields</h4>
						<div class="container p-0" style="padding-right: 50px !important;">
							<schema-derived-field v-for="(field,index) in parentCompiledFields" :key="field.$id" :field="field" :override="overrideFields[field.$id]" :index="index" :schemas="schemaLookup" :override-lookup="overrideFields" :add-field-function="addSchemaField" :schema="schemaSettings" :source="parentCompiledFields" />
						</div>
					</template>
					<hr>
					<button class="btn btn-success mt-2" @click.prevent="saveSchemaAndFields"><i class="fas fa-save"></i> Save Schema and Fields</button>
					<button class="btn btn-secondary mt-2" @click.prevent="resetSchema"><i class="fas fa-sync-alt"></i> Reset Editor</button>
					<button class="btn btn-primary mt-2" @click.prevent="extendSchema"><i class="fas fa-seedling"></i> Extend Schema</button>
					<button class="btn btn-danger float-right mt-2" @click.prevent="deleteThisSchema"><i class="fas fa-times"></i> Delete Schema</button>
				</form>
			</div>
		</div>

		<div class="card shadow-sm">
			<div class="card-header">
				<h4 class="my-0 font-weight-normal">Schema Settings (JSON)</h4>
			</div>
			<div class="card-body">
				<div id="editor"></div>
				<button class="btn btn-success mt-4" @click.prevent="saveEditorSchema"><i class="fas fa-save"></i> Save Schema</button>
				<button class="btn btn-secondary mt-4" @click.prevent="resetSchema"><i class="fas fa-sync-alt"></i> Reset Editor</button>
			</div>
		</div>
	</div>
</template>

<script>
import 'ace-builds';
import 'ace-builds/webpack-resolver';

import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { toJsonDate } from '../util';
import _ from 'lodash';
import { uuid } from 'uuidv4';

import SchemaEditorField from "@/components/utilities/SchemaEditorField";
import SchemaDerivedField from "@/components/utilities/SchemaDerivedField";

export default {
	name: "project-schema",
	props: [ 'nodeId', 'projectId', 'schemaName' ],
	components: {
		SchemaEditorField,
		SchemaDerivedField
	},
	data() {
		return {
			schemaSettings: { },	// Schema settings, without fields.
			standardFields: [],		// List of all standard fields of this schema (sorted out from schemaSettings.fields)
			parentCompiledFields: [],	// List of all compiled fields of all ascendants
			overrideFields: { },	// A lookup of new/existing override fields, keyed on the base field each overrides.
			fieldsToDelete: [],
			showMixins : false,
			editorMixins : null
		};
	},
	watch:
	{
		schemaSettings:
		{
			deep: true,
			handler: function( newValue, oldValue )
			{
				this.setEditorSchema( );
			}
		},
		standardFields: {
			deep: true,
			handler: function( newVal, oldVal )
			{
				// const newField = newVal[newVal.length-1];
				
				this.setJSONFields( newVal, this.overrideFields );
				this.editor.setValue( JSON.stringify( this.schemaSettings, null, 2 ), -1 );
			}
		},
		overrideFields: {
			deep: true,
			handler: function( newVal, oldVal )
			{
				this.setJSONFields( this.standardFields, newVal );
				this.editor.setValue( JSON.stringify( this.schemaSettings, null, 2 ), -1 );
			}
		},
		'schemaSettings.abstract': function( newVal, oldVal )
		{
			if( newVal !== true )
				this.$delete( this.schemaSettings, 'abstract' );
		}
	},
	computed:
	{
		standardFieldsHierarchical: function( )
		{
			const fieldsById = this.standardFields.reduce( ( lookup, f ) => { lookup[f.$id] = f; return lookup; }, {} );

			this.standardFields.forEach( f => { this.$set( f, 'subfields', [] ) } );

			this.standardFields.forEach( ( f => {
				if( f.parent )
				{
					if( fieldsById[f.parent] )
						this.$set( fieldsById[f.parent].subfields, fieldsById[f.parent].subfields.length, f );
					else
					{
						const derivedParentField = this.parentCompiledFields.find( pf => pf.$id === f.parent );
						if( derivedParentField )
						{
							if( !Array.isArray( derivedParentField.subfields ) )
								this.$set( derivedParentField, 'subfields', [] );
							if( !derivedParentField.subfields.find( existing => _.isEqual( existing, f ) ) )
							{
								f.owner = this.schemaSettings.$id;
								this.$set( derivedParentField.subfields, derivedParentField.subfields.length, f );
								// console.log( "## Set New Field:", derivedParentField, this.standardFields );
							}
						}
					}
				}
			}).bind(this) );
			return this.standardFields.filter( f => !f.hasOwnProperty( 'parent' ) );
		},
		currentSessionAccount: function( )
		{
			try {
				let sessionGuid = this.activeSession( this.nodeId );
				let [ sNode, sKey ] = sessionGuid.split( "/" );
				let session = this.session( sessionGuid );
				let account = this.account( sNode, session.account );
				if( account )
					return account;
			} catch( err ) { }
			return { };	// No session.
		},
		...mapGetters( "accounts", {
			account: "account",
		}),
		...mapGetters( "authentication", {
			activeSession: "activeSession",
			session: "session"
		}),
		...mapGetters( "collections", {
			collections: "collections",
		}),
		...mapGetters( "schemas", {
			schemas: "schemas", // map STATE schemas/schemas to "schemas" AS A FUNCTION, which requires the nodeId and projectId as inputs!
		}),
		collectionList( ) {
			return this.collections( this.nodeId, this.projectId );
		},
		schemaList( )
		{
			return this.schemas( this.nodeId, this.projectId );
		},
		schemaLookup( )
		{
			const lookup = { };
			const schemas = this.schemaList || [];
			for( const s of this.schemaList )
			{
				const name = s.$id.split(':')[1];
				lookup[name] = s;
			}
			return lookup;
		}
	},
	methods:
	{
		toggleMixins( state )
		{
			const show = ( typeof state === 'boolean' ? state : !this.showMixins );
			
			if( !show )
			{
				this.editorMixins.destroy( );
				this.editorMixins = null;
			}

			this.showMixins = !!show;
			
			if( show )
			{
				this.$nextTick( ( () => {
					this.editorMixins = ace.edit( "editor-mixins-"+ this._uid, {
						mode: "ace/mode/json",
						theme: "ace/theme/tomorrow",	//"ace/theme/dracula",
						maxLines: 20,
						minLines: 5,
						fontSize: 14,
						showPrintMargin : false,
						
					});
					const mixins = _.cloneDeep( this.schemaSettings.$mixins || { } );
					for( let mf in mixins )	// Remove all $new fields.
						try {
							delete mixins[mf].$new;
						} catch( err ) { }
					this.editorMixins.setValue( JSON.stringify( mixins || { }, null, 2 ), -1 );
					this.editorMixins.getSession().on( 'change', (e) => {
						try {
							const mixins = JSON.parse( this.editorMixins.getValue( ) );
							for( let mf in mixins )	// Re-add all the $new fields.
								try {
									if( !mixins[mf].$delete && this.schemaSettings.$id )
										mixins[mf].$new = true;
								} catch( err ) { }
							this.$set( this.schemaSettings, '$mixins', mixins );
						} catch( err ) { /* Oh well.. */ }
					});
				} ).bind(this) );
			}
		},
		setJSONFields( standardFields, overrideFields )
		{
			this.$set( this.schemaSettings, 'fields', [] );
			const fields = this.schemaSettings.fields;
			this.fieldsToDelete = [];

			for( let i=0; i < standardFields.length; i++ )
			{
				if( standardFields[i]._delete && standardFields[i].$id )
				{
					this.$set( this.fieldsToDelete, this.fieldsToDelete.length, standardFields[i].$id );
					continue;	// Skip further processing.
				}
				const field = _.cloneDeep( standardFields[i] );
				field.$schema = "$field.$standard.";
				this.$set( fields, fields.length, field );
			}

			for( let baseFieldId in overrideFields )
			{
				if( overrideFields[baseFieldId]._override !== true )
				{
					const id = overrideFields[baseFieldId].$id;
					if( id )
						this.$set( this.fieldsToDelete, this.fieldsToDelete.length, id );
					continue;	// Skip non-committed fields.
				}
				const field = _.cloneDeep( overrideFields[baseFieldId] );
				delete field._override;
				if( Object.keys( field ).length === 0 )
					continue;	// Skip fields with no override properties.
				field.overrides = baseFieldId;
				field.$schema = "$field.$override.";
				this.$set( fields, fields.length, field );
			}
		},
		flattenCompiledFields( fields )
		{
			let result = [ ];
			if( Array.isArray( fields ) )
				for( const f of fields )
					result = result.concat( [ f, ...this.flattenCompiledFields( f.subfields ) ] );
			return result;
		},
		async updateParentCompiledFields( )
		{
			const options = { compiled : true };
			const name = this.getSchemaParent( );
			try {
				const compiledSchema = await this.readSchema( { nodeId : this.nodeId, projectId : this.projectId, name, nocache : true, options } );
				this.parentCompiledFields = compiledSchema.compiledfields;
				const allParentFields = this.flattenCompiledFields( this.parentCompiledFields )
				console.log( "$$ parentCompileFields:", allParentFields.length );
				for( let i=0; i < allParentFields.length; i++ )
				{
					const field = allParentFields[i];
					if( !this.overrideFields[field.$id] )
						this.$set( this.overrideFields, field.$id, { _override : false } );
				}
			} catch( err ) {
				console.warn( `Could not retrieve the compiled schema [${name}]. Details:` );
				console.error( err );
				this.parentCompiledFields = [];
			}
		},
		getSchemaParent( schemaName )
		{
			schemaName = schemaName || this.schemaName;
			const parts = schemaName.split( '.' );
			if( !parts[parts.length-1] )
				parts.pop( );
			if( parts.length > 1 )
				parts.pop( );
			return parts.join( '.' ) +'.';
		},
		isRootSchema( schemaName )
		{
			schemaName = schemaName || this.schemaName;
			return this.getSchemaParent( schemaName ) === schemaName;
		},
		addSchemaField( parentFieldId, domContainer )
		{
			const source = this.standardFields;
			const newEntry = { name : "" };
			if( parentFieldId )
			{
				newEntry.parent = parentFieldId;
				console.log( "FOCUS SUBFIELD:", parentFieldId, domContainer );
			}
			this.$set( source, source.length, newEntry );
			console.log( "New field:", newEntry );
			const $vue = this;
			this.$nextTick( function( ) {
				try {
					const fields = ( domContainer || $vue.$refs.schemaFields ).children;
					for( let i=fields.length-1; i >= 0; i-- )
					{
						if( fields[i].tagName.toLowerCase( ) === 'div' )
						{
							fields[i].getElementsByTagName( "input" )[0].focus( );
							return;
						}
					}
				} catch( err ) { console.log( err ); }
			} );
		},
		deleteSchemaField( index )
		{
			// WIP

			// const field = this.schemaSettings.fields[index];
			// console.log( "Deleting Field", index, field );
			// if( field.parent )
			// {
			// 	const parentField = this.schemaSettings.fields.find( f => f.$id === field.parent );
			// 	if( parentField && Array.isArray( parentField.subfields ) )
			// 	{
			// 		const deleteIndex = parentField.subfields.findIndex( sf => _.isEqual( sf, field ) )
			// 		if( deleteIndex > -1 )
			// 			this.$delete( parentField.subfields, deleteIndex );
			// 	}
			// }
			this.schemaSettings.fields.splice( index, 1 );
		},

		async refreshSchema( )
		{
			// TODO: Start Loading
			await this.readSchema( { nodeId : this.nodeId, projectId : this.projectId, name : this.schemaName, nocache : true } );
			this.resetSchema( );
			await this.updateParentCompiledFields( );
			// TODO: End Loading
		},
		extendSchema( )
		{
			this.$router.push( { name : 'schemas', params : { nodeId : this.nodeId, projectId : this.projectId, derivedSchema : this.schemaSettings.$id } } );
		},
		resetSchema( )
		{
			this.standardFields = [];
			this.overrideFields = {};
			const schema = _.cloneDeep( this.getSchema( ) );
			if( Array.isArray( schema.fields ) )
				for( let i=0; i < schema.fields.length; i++ )
				{
					const field = schema.fields[i];
					if( field.$schema.indexOf( '.$override.' ) > -1 )
					{
						field._override = true;
						this.$set( this.overrideFields, field.overrides, field );
					}
					else if( field.$schema.indexOf( '.$standard.' ) > -1 )
						this.$set( this.standardFields, this.standardFields.length, field );
					else
						console.warn( "Invalid field found (ignoring): "+ JSON.stringify( field, null, 2 ) );
				}
			delete schema.fields;
			this.schemaSettings = schema;
		},
		async deleteThisSchema( )
		{
			if( !confirm( "Are you sure you want to delete this schema?" ) )
				return;
			try {
				let result = await this.deleteSchema( { nodeId : this.nodeId, projectId : this.projectId, name : this.schemaName } );
				if( !result )
					console.warn( `[${this.schemaName}] schema was not found at deletion! Redirecting to schemas listing anyway.` );
				this.$router.push( { name : 'schemas', params : { nodeId : this.nodeId, projectId : this.projectId } } );
			} catch( err ) {
				console.error( err );
				alert( "Schema could not be deleted!\n"+ err.message );
			}
		},
		getSchema( )
		{
			return this.$store.getters['schemas/schema']( this.nodeId, this.projectId, this.schemaName );
		},
		setEditorSchema( schema )
		{
			schema = schema || this.schemaSettings;
			if( schema && this.editor )
			{
				let editorSchema = _.omit( schema, [ '$created', '$modified', '$version', '$node' ] );
				this.editor.setValue( JSON.stringify( editorSchema, null, 2 ), -1 );
			}
		},
		async saveSchemaAndFields( )
		{
			const schema = this.schemaSettings;
			const id = this.getSchema( ).$id;
			const fields = schema.fields || [];

			const schemaName = this.schemaName;
			const name = schemaName;
			const projectId = this.projectId;
			const nodeId = this.nodeId;

			try {
				let updates = _.omit( schema, [ 'fields', '$created', '$modified', '$version', '$node' ] );
				if( !updates.description )
					updates.description = "";
				if( !updates.abstract )
					updates.abstract = { $delete : true };
				if( Object.keys( updates ).length > 0 )
				{
					let result = await this.updateSchema( { nodeId, projectId, name, updates } );
					if( null === result )
						throw new Error( "This schema is no longer found in the repository!" );
				}
				
				for( let i=0; i < fields.length; i++ )
				{
					const f = fields[i];
					delete f.subfields;
					if( f.$id )
						try{
							await this.replaceField( { nodeId, projectId, schemaName, fieldId : f.$id, newField : f } )
						} catch( err ) { console.error( err ); throw new Error( `Could not replace field [${f.$id}]: ${err.message}` ); }
					else
						try{
							await this.createField( { nodeId, projectId, schemaName, newField : f } );
						} catch( err ) { console.error( err ); throw new Error( `Could not create field [${f.$id||f.name||f.$schema}]: ${err.message}` ); }
				}

				for( let i=0; i < this.fieldsToDelete.length; i++ )
				{
					const fieldId = this.fieldsToDelete[i];
					try{
						await this.deleteField( { nodeId, projectId, schemaName, fieldId } )
					} catch( err ) { console.error( err ); throw new Error( `Could not delete field [${f.$id}]: ${err.message}` ); }
				}

				this.fieldsToDelete = [];

				alert( "Schema and Fields Saved!" );

				// TODO: close loading overlay
			} catch( err ) {

				// TODO: Switch loading overlay to ERROR overlay.
				console.warn( "Schema save failed:", err );

				alert( "ERROR!! Failed to save schema:\n"+ err.message );

				// TODO: Set this action, as the result of dismissing the ERROR OVERLAY
				// this.$router.push( { name : 'schemas', params : { nodeId : this.nodeId, projectId : this.projectId } } );
			}
		},
		async saveEditorSchema( )
		{
			// TODO: Set loading overlay
			let schema;

			try {
				schema = JSON.parse( this.editor.getValue( ) );
			} catch( err ) {
				alert( "Could not save the object:\n"+ err.message );
				console.error( err );
				return;
			}

			try {
				if( !schema.abstract )
					schema.abstract = { $delete : true };
				let result = await this.updateSchema( { nodeId : this.nodeId, projectId : this.projectId, name : this.schemaName, updates : schema } );
				if( null === result )
					throw new Error( "This schema is no longer found in the repository!" );
				
				// TODO: close loading overlay
				alert( "Schema and Fields Saved!" );

			} catch( err ) {

				// TODO: Switch loading overlay to ERROR overlay.

				console.warn( "Schema save failed:", err );

				alert( "ERROR!! Failed to save schema:\n"+ err.message );

				// TODO: Set this action, as the result of dismissing the ERROR OVERLAY
				// this.$router.push( { name : 'schemas', params : { nodeId : this.nodeId, projectId : this.projectId } } );
			}
		},
		async loadSchemas( nocache )
		{
			try {
				await this.listSchemas( { nodeId : this.nodeId, projectId : this.projectId, nocache : nocache } );
			} catch( err ) {
				if( err.response && ( err.response.status === 401 ) )
				{
					console.warn( "Session expired, invalid or missing.." );
					this.checkMySession( );
				}
				else
					throw err;
			}
		},
		...mapActions({
			readSchema: "schemas/read",
			listSchemas: "schemas/list",
			updateSchema: "schemas/update",
			deleteSchema: "schemas/delete",
			listCollections: "collections/list",
			replaceField: "schemas/replaceField",
			createField: "schemas/createField",
			deleteField: "schemas/deleteField",
		}),
	},
	created( )
	{
		this.loadSchemas( true );	// Reload schemas in the background to make sure we have the latest.
	},
	async mounted( )
	{
		try {
			this.editor = ace.edit( "editor", {
				mode: "ace/mode/json",
				theme: "ace/theme/tomorrow",	//"ace/theme/dracula",
				maxLines: 20,
				minLines: 5,
				fontSize: 14,
				showPrintMargin : false
			});
			await this.listCollections( { nodeId : this.nodeId, projectId : this.projectId } );
			await this.refreshSchema( );
		} catch( err ) { throw err; }
	}
};
</script>
<style scoped>
div#editor
{
	border: 1px solid #666;
}
h2 {
	font-weight: normal;
}
</style>


// WEBPACK FOOTER //
// src/components/Schema.vue